import { useState, useEffect, useRef, useMemo } from "react";
import { Search } from "@/lib/icons";
import { elements, categoryLabels, type Element } from "@/data/elements";

interface Props {
  onSelect: (element: Element) => void;
}

export default function GlobalSearch({ onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // / shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "/" && !e.ctrlKey && !e.metaKey && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return elements.filter((el) =>
      el.name.toLowerCase().includes(q) ||
      el.symbol.toLowerCase() === q ||
      String(el.number) === q ||
      categoryLabels[el.category].toLowerCase().includes(q) ||
      el.phase.includes(q) ||
      el.block === q
    ).slice(0, 12);
  }, [query]);

  const handleSelect = (el: Element) => {
    onSelect(el);
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      {/* Search trigger */}
      <button
        onClick={() => setOpen(true)}
        className="w-full max-w-md mx-auto mb-4 flex items-center gap-2 h-10 px-4 rounded-lg border border-input bg-tile-bg text-sm text-muted-foreground hover:border-primary/50 transition-colors"
        aria-label="Search elements. Press forward slash to open."
      >
        <Search size={14} aria-hidden="true" />
        <span className="flex-1 text-left">Search elements...</span>
        <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono text-muted-foreground" aria-hidden="true">/</kbd>
      </button>

      {/* Search overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] bg-background/60 backdrop-blur-sm"
          onClick={() => { setOpen(false); setQuery(""); }}
          role="dialog"
          aria-modal="true"
          aria-label="Search elements"
        >
          <div className="w-full max-w-lg mx-4 bg-popover border border-border rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 h-12 border-b border-border">
              <Search size={16} className="text-muted-foreground shrink-0" aria-hidden="true" />
              <label htmlFor="global-search-input" className="sr-only">Search by name, symbol, number, category, phase, or block</label>
              <input
                ref={inputRef}
                id="global-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, symbol, number, category, phase, or block..."
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
              <kbd className="text-[10px] font-mono text-muted-foreground px-1.5 py-0.5 rounded bg-muted" aria-hidden="true">ESC</kbd>
            </div>

            {results.length > 0 && (
              <ul className="max-h-80 overflow-y-auto p-2" role="listbox" aria-label="Search results">
                {results.map((el) => (
                  <li key={el.number} role="option" aria-selected={false}>
                    <button
                      onClick={() => handleSelect(el)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left hover:bg-accent transition-colors"
                      aria-label={`${el.name}, symbol ${el.symbol}, atomic number ${el.number}`}
                    >
                      <span className="w-10 h-10 rounded-md bg-muted flex flex-col items-center justify-center shrink-0">
                        <span className="text-xs font-semibold text-foreground">{el.symbol}</span>
                        <span className="text-[9px] text-muted-foreground">{el.number}</span>
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground">{el.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {categoryLabels[el.category]} · {el.phase} · {el.block}-block · {el.mass.toFixed(3)} u
                        </p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {query && results.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                No elements found for "{query}"
              </div>
            )}

            {!query && (
              <div className="p-4 text-center text-xs text-muted-foreground">
                Type to search by name, symbol, atomic number, category, phase, or block
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
